import React, { useState, useEffect, useContext, useMemo } from "react";
import "./review.css";
import Avatar from "../../assets/avatar.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import Rating from "@mui/material/Rating";
import Pagination from "@mui/material/Pagination";
import ReviewAddModal from "./ReviewAddModal";
import { getAllReviews } from "../../api/axios";
import nprogress from "nprogress";
import "nprogress/nprogress.css";
import { Spinner } from "react-bootstrap";
import { AuthContext } from "../../context/AuthContext";

const Reviews = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [openReviewAddModal, setOpenReviewAddModal] = useState(false);
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [loading, setLoading] = useState(true);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Fetch all reviews
  const GetAllReviews = async () => {
    nprogress.start();
    try {
      const data = await getAllReviews();
      if (data.success) {
        setReviews(data.users);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error); 
    } finally {
      nprogress.done();
      setLoading(false);
    }
  };

  useEffect(() => {
    GetAllReviews();
  }, []);

  // Update screen size on window resize
  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Pagination
  const handleChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Constants for pagination and layout
  const reviewsPerColumnLarge = 4;
  const reviewsPerColumnMedium = 4;
  const reviewsPerPageLarge = 12;
  const reviewsPerPageMedium = 8;
  const reviewsPerPageSmall = 8;

  // Split reviews into columns based on screen size
  const splitIntoColumns = (reviews) => {
    if (screenSize < 577) {
      return [reviews];
    }

    const isMediumScreen = screenSize >= 577 && screenSize <= 992;
    const columns = isMediumScreen ? [[], []] : [[], [], []];
    const reviewsPerColumn = isMediumScreen
      ? reviewsPerColumnMedium
      : reviewsPerColumnLarge;

    reviews.forEach((review, index) => {
      columns[Math.floor(index / reviewsPerColumn) % columns.length].push(review);
    });

    return columns;
  };

  // Paginate reviews based on screen size
  const paginatedReviews = useMemo(() => {
    const isMediumScreen = screenSize >= 577 && screenSize <= 992;
    const isSmallScreen = screenSize < 577;
    const reviewsPerPage = isSmallScreen
      ? reviewsPerPageSmall
      : isMediumScreen
        ? reviewsPerPageMedium
        : reviewsPerPageLarge;

    return reviews?.slice((page - 1) * reviewsPerPage, page * reviewsPerPage);
  }, [reviews, page, screenSize]);
  // console.log('paginatedReviews: ', paginatedReviews);

  // Split paginated reviews into columns
  const columns = useMemo(() => splitIntoColumns(paginatedReviews), [paginatedReviews, screenSize]);

  // Open review add modal
  const handleClickAddReview = () => {
    setOpenReviewAddModal(true);
  };

  if (loading) {
    return (
      <div className="spinner-page">
        <Spinner className="spinner" />
      </div>
    );
  }

  console.log('Rendering Reviews - reviews:', reviews);

  return (
    <div className="reviews-full-wrapper">
      <div className="reviews-container">
        <div>
          <span className="all-role-count">{reviews?.length} Reviews</span>
        </div>
        {isAuthenticated && (
          <div>
            <button className="homeMiddle2" onClick={handleClickAddReview}>
              TELL YOUR STORY
            </button>
          </div>
        )}
        <div className="seperate-line"></div>
        <div className="review-list-container">
          {paginatedReviews?.map((review, reviewIndex) => (
            <div className="review-review-card" key={reviewIndex}>
              <div className="review-authImge">
                <img
                  src={review?.review?.User?.imageURL || Avatar}
                  alt={`${review?.review?.User?.firstName || "User"}'s avatar`}
                  className="review-auth-avatar"
                  onError={(e) => { e.target.onerror = null; e.target.src = Avatar; }}
                />
                <span className="review-authName">
                  {review?.review?.User?.firstName || ""}{" "}
                  {review?.review?.User?.lastName || ""}
                </span>
              </div>
              <div className="review-content">
                <div className="review-header">
                  <span>
                    <FontAwesomeIcon icon={faCalendarAlt} />{" "}
                    {review?.review?.createdDate || " "}
                  </span>
                  <Rating
                    name="read-only"
                    value={review?.review?.rateValue || 0}
                    readOnly
                    sx={{ fontSize: "18px", color: '#6e0605' }}
                  />
                </div>
                <div className="seperate-line"></div>
                <span className="review-description">
                  {review?.review?.description || " "}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="pagination">
          <Pagination
            count={Math.ceil(
              reviews.length /
              (screenSize < 577
                ? reviewsPerPageSmall
                : screenSize >= 577 && screenSize <= 992
                  ? reviewsPerPageMedium
                  : reviewsPerPageLarge)
            )}
            boundaryCount={1}
            page={page}
            onChange={handleChange}
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#e54447",
                "&:hover": {
                  backgroundColor: "rgba(229, 68, 71, 0.1)",
                },
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#e54447",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#d43d3f",
                },
              },
              "& .MuiPaginationItem-ellipsis": {
                color: "#e54447",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              },
            }}
          />
        </div>
        <ReviewAddModal
          openReviewAddModal={openReviewAddModal}
          setOpenReviewAddModal={setOpenReviewAddModal}
          GetAllReviews={GetAllReviews}
        />
      </div>
    </div>
  );
};

export default Reviews;